import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Play, Heart, ArrowLeft, Film, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ReviewSection from '../components/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = { cartoon: 'Cartoon', live_action: 'Live Action' };
const eraLabels = { early_90s: 'Early 90s', late_90s: 'Late 90s', early_2000s: 'Early 2000s' };
const placeholder = 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 400 225%22><rect width=%22400%22 height=%22225%22 fill=%22%23FDFBEC%22/><text x=%22200%22 y=%22118%22 font-family=%22sans-serif%22 font-size=%2218%22 fill=%22%234B5563%22 text-anchor=%22middle%22>Retro Playground TV</text></svg>';

export default function ShowDetailPage() {
  const { showId } = useParams();
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/shows/${showId}`),
      axios.get(`${API}/shows/${showId}/episodes`),
    ]).then(([showRes, epRes]) => {
      setShow(showRes.data);
      setEpisodes(epRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [showId]);

  useEffect(() => {
    if (user && showId) {
      axios.get(`${API}/favorites/check/${showId}`, { withCredentials: true })
        .then(r => setIsFavorite(r.data.is_favorite))
        .catch(() => {});
    }
  }, [user, showId]);

  const toggleFavorite = async () => {
    if (!user) { toast.error('Sign in to save favorites'); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${showId}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/${showId}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Added to favorites!');
      }
    } catch {
      toast.error('Failed to update favorites');
    }
  };

  if (loading) return (
    <div className="page-content flex items-center justify-center min-h-[60vh]">
      <div className="animate-pulse text-center">
        <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4" />
        <div className="h-4 bg-gray-200 rounded w-32 mx-auto" />
      </div>
    </div>
  );

  if (!show) return (
    <div className="page-content flex items-center justify-center min-h-[60vh]">
      <p className="font-['Fredoka'] text-2xl text-[#4B5563]">Show not found</p>
    </div>
  );

  return (
    <div className="page-content">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8">
        {/* Back link */}
        <Link to="/browse" className="inline-flex items-center gap-1 text-sm font-bold text-[#FF3860] hover:underline mb-6" data-testid="back-to-browse">
          <ArrowLeft size={16} strokeWidth={2.5} /> Back to Browse
        </Link>

        {/* Show header */}
        <div className="neo-card p-6 md:p-8 mb-8 bg-[#FDFBEC]" data-testid="show-detail-header">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-72 shrink-0">
              <div className="aspect-video rounded-xl border-2 border-[#0A0A0A] overflow-hidden shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
                <img
                  src={show.thumbnail_url || placeholder}
                  alt={show.title}
                  className="w-full h-full object-cover vhs-thumbnail"
                  onError={(e) => { e.target.src = placeholder; }}
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="font-['Fredoka'] text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight mb-3">
                    {show.title}
                  </h1>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md border-2 border-[#0A0A0A] bg-[#FF3860] text-white shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                      {categoryLabels[show.category] || show.category}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md border-2 border-[#0A0A0A] bg-[#4AC6FF] text-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                      {eraLabels[show.era] || show.era}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md border border-[#0A0A0A]/20 bg-[#F4F4F5] text-[#4B5563] font-['IBM_Plex_Mono']">
                      {show.episode_count || 0} episode{(show.episode_count || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <button
                  onClick={toggleFavorite}
                  className={`neo-btn p-3 rounded-xl ${isFavorite ? 'bg-[#FF3860] text-white' : 'bg-white text-[#0A0A0A]'}`}
                  data-testid="toggle-favorite-btn"
                >
                  {isFavorite ? <Heart size={20} strokeWidth={2.5} fill="currentColor" /> : <Heart size={20} strokeWidth={2.5} />}
                </button>
              </div>
              <p className="text-base text-[#4B5563] font-['Nunito'] leading-relaxed">{show.description}</p>
            </div>
          </div>
        </div>

        {/* Episodes */}
        <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-6" data-testid="episodes-heading">
          Episodes
        </h2>
        {episodes.length === 0 ? (
          <div className="neo-card p-8 text-center bg-white">
            <Film size={40} className="text-[#4B5563] mx-auto mb-3" strokeWidth={1.5} />
            <p className="font-['Fredoka'] text-lg text-[#4B5563]">No episodes available yet</p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children">
            {episodes.map((ep, i) => (
              <Link
                key={ep.id}
                to={`/watch/${ep.id}`}
                className="neo-card flex items-center gap-4 p-4 bg-white animate-fade-in-up group"
                style={{ opacity: 0, animationDelay: `${i * 60}ms` }}
                data-testid={`episode-${ep.id}`}
              >
                <div className="w-10 h-10 rounded-lg border-2 border-[#0A0A0A] bg-[#FF3860] text-white flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] shrink-0 group-hover:bg-[#FFDE00] group-hover:text-[#0A0A0A] transition-colors">
                  <Play size={16} strokeWidth={2.5} fill="currentColor" className="ml-0.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] truncate">
                    S{ep.season}E{ep.episode_number} - {ep.title}
                  </h3>
                  {ep.description && (
                    <p className="text-sm text-[#4B5563] font-['Nunito'] truncate">{ep.description}</p>
                  )}
                </div>
                {ep.duration && (
                  <span className="flex items-center gap-1 text-xs text-[#4B5563] font-['IBM_Plex_Mono'] shrink-0">
                    <Clock size={12} strokeWidth={2.5} /> {ep.duration}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Reviews & Ratings */}
        <ReviewSection showId={showId} />
      </div>
    </div>
  );
}
